import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@beweegbaas/ui/lib/utils";

const dotVariants = cva("relative inline-block size-2 shrink-0 rounded-full", {
  variants: {
    variant: {
      solid: "bg-(--dot-color)",
      ring: "border-[1.5px] border-(--dot-color)",
    },
  },
  defaultVariants: {
    variant: "solid",
  },
});

type DotProps = React.ComponentProps<"span"> &
  VariantProps<typeof dotVariants> & {
    /** Any CSS colour; without it the dot takes the text colour, so a `text-*` class colours it. */
    color?: string;
    /** Something going on right now: a ring pulses out of the dot. */
    pulse?: boolean;
  };

/**
 * A small round marker in a colour, before what it marks: filled, or a ring. It is decoration:
 * hidden from a screen reader, which reads what it marks instead.
 */
function Dot({ color, variant, pulse = false, className, style, ...props }: DotProps) {
  return (
    <span
      aria-hidden
      data-slot="dot"
      className={cn("[--dot-color:currentColor]", dotVariants({ variant }), className)}
      style={color ? ({ "--dot-color": color, ...style } as React.CSSProperties) : style}
      {...props}
    >
      {pulse ? (
        <span className="absolute inset-0 rounded-full bg-inherit opacity-75 motion-safe:animate-ping" />
      ) : null}
    </span>
  );
}

export { Dot, dotVariants };
